const { jsonToGraphQLQuery, VariableType } = require('json-to-graphql-query');
const graphql = require('./graphql');
const introspectionApi = require('./introspectionApi');
const queryHelper = require('./queryHelper');
const { camelCase, pascalCase } = require('change-case');
const { cloneDeep, get, set } = require('lodash');
const { SchemaTypes } = introspectionApi;

const getAttribute = (eedmRecord, attrName) => {
	return get(eedmRecord, attrName);
};
const variablesByAttribute = (sort = []) => {
	const variables = {
		limit: 'Int',
		offset: 'Int',
	}
	if (sort && sort.length > 0 ) {
		variables['sortDirection'] = 'SortOrder'
	}
	return variables;
};

const queryByAttribute = (filter = {}, sort = []) => {
	const querySort = sort.map(x => set({}, x, new VariableType('sortDirection')));
	return {
		XX_QUERY_NAME_XX: {
			__args: {
				filter: {
					...filter
				},
				limit: new VariableType('limit'),
				offset: new VariableType('offset'),
				sort: querySort,
			},
			edges: {
				node: {
					id: true,
				}
			},
			totalCount: true,
		},
		__variables: variablesByAttribute(sort),
	}
};

const queryByIds = ((filter = {}) => {
	return {
		XX_QUERY_NAME_XX: {
			__args: {
				filter: {
					id: { IN: new VariableType('idList') },
					...filter
				},
			},
			edges: {
				node: {
					id: true,
				}
			},
		},
		__variables: { idList: '[ID]' },
	}
});

const variablesByFilter = {
	contains: 'String',
	limit: 'Int',
	offset: 'Int',
	sortDirection: 'SortOrder',
};

const querybyFilter = {
	XX_QUERY_NAME_XX: {
		__args: {
			filter: {
				XX_FILTER_ATTR_NAME_XX: { CONTAINS: new VariableType('contains') },
			},
			limit: new VariableType('limit'),
			offset: new VariableType('offset'),
			sort: [
				{ XX_ATTR_NAME_XX: new VariableType('sortDirection') },
			],
		},
		edges: {
			node: {
				__on: {
					__typeName: '',
					id: true,
				},
			},
		},
		totalCount: true,
	},
	__variables: variablesByFilter,
};

/**
 * Get query for retrieving values by filter.
 */
const getDefaultFilter = (graphType, attributes, sortAttribute, filterText, filterAttribute, includeTotalCount) => {
	return {
		getAttribute,
		query: populateDefaultFilterQuery(graphType, attributes, sortAttribute, filterText, filterAttribute, includeTotalCount),
	};
};

const populateDefaultFilterQuery = (graphType, attributes, sortAttribute, filterText, filterAttribute, includeTotalCount) => {
	// Replace 'XX_QUERY_NAME_XX' key with the graphtype
	let query = cloneDeep(querybyFilter);
	const rootQuery = query.XX_QUERY_NAME_XX;
	query[graphType] = rootQuery;
	delete query.XX_QUERY_NAME_XX;
	query[graphType].edges.node.__on.__typeName = pascalCase(graphType);

	// Add attributes
	if (attributes) {
		const attribs = Array.isArray(attributes) ? attributes : [attributes];
		attribs.forEach(attrib => {
			// Attributes may be of type string of object
			// For string, just add it as a property to edges with a true value
			if (typeof attrib === 'string') {
				query[graphType].edges.node.__on[attrib] = true;
			}
			// For object, append the object to the edges
			else if (typeof attrib === 'object') {
				query[graphType].edges.node.__on = {
					...query[graphType].edges.node.__on,
					...attrib,
				}
			}
		});
	}
	// Sort attribute
	const sortValue = query[graphType].__args.sort[0].XX_ATTR_NAME_XX;
	if (sortAttribute) {
		query[graphType].__args.sort.push({ [sortAttribute]: sortValue });
	} else {
		delete query.__variables.sortDirection;
	}
	query[graphType].__args.sort.shift();

	// Filter text
	if (filterText && filterText.trim() && filterAttribute) {
		const filterNode = query[graphType].__args.filter.XX_FILTER_ATTR_NAME_XX;
		query[graphType].__args.filter[filterAttribute] = filterNode;
	} else {
		delete query.__variables.contains;
	}
	delete query[graphType].__args.filter.XX_FILTER_ATTR_NAME_XX;

	// Total count
	if (!includeTotalCount) {
		delete query[graphType].totalCount;
	}

	return query;
};

/**
 * Fetch 'title' attribute values of EEDM lookup model using GraphQL
 */
const fetchEedmLookupValues = async (api, graphType, filter, sort = ['title'], sortOptions, includeTotalCount, schemaType) => {
	const results = await fetchEedmValuesByAttribute(api, graphType, 'title', filter, sort, sortOptions, includeTotalCount, schemaType);
	return results;
};

/**
 * Fetch values for a given attribute of EEDM model using GraphQL
 */
const fetchEedmValuesByAttribute = async (
	api,
	graphType,
	attributeNames,
	filter = {},
	sort = [],
	sortOptions = {},
	includeTotalCount = false,
	schemaType = SchemaTypes.Filter
) => {
	if (!api || (!api.webFetch && !api.apiFetch) || !graphType || !attributeNames) {
		return [];
	}

	const graphTypeName = schemaType === SchemaTypes.Filter ? introspectionApi.getFetchTypeName(graphType) : graphType;
	const graphTypemaxVersion = await introspectionApi.getMaxVersionNameForBaseName(api, graphTypeName, schemaType);
	if (!graphTypemaxVersion) { return []; }

	// remove F_ for schema type of Filter and camelize
	const queryName = schemaType === SchemaTypes.Filter ? camelCase(graphTypemaxVersion.substring(2)) : camelCase(graphTypemaxVersion);

	let query = cloneDeep(queryByAttribute(filter, sort))
	if (attributeNames) {
		query.XX_QUERY_NAME_XX.edges.node[attributeNames] = true;
	}
	if (!includeTotalCount) {
		delete query.XX_QUERY_NAME_XX.totalCount;
	}
	query = jsonToGraphQLQuery({ query });
	query = query.replace('XX_QUERY_NAME_XX', queryName);

	const criteriaVariables = queryHelper.getCriteriaVariablesRelay({
		order: 'asc',
		page: 0,
		rowsPerPage: 10000,
		...sortOptions
	});

	const variables = Object.assign({}, criteriaVariables);
	try {
		const response = await graphql.query({
			query,
			api: api,
			variables,
		});
		const { data, errors } = queryHelper.response(response);
		if (errors) { throw errors; }

		const results = queryHelper.relayToListResponse(data[queryName]);
		return buildResults(results, attributeNames);

	} catch (err) {
		throw err;
	}
};

/**
 * Fetch values for a given attribute of EEDM model based on a list of IDs
 */
const fetchEedmValuesByIds = async (api, graphType, idList, attributeNames, filter = {}, schemaType = SchemaTypes.Filter) => {
	if (!api || (!api.webFetch && !api.apiFetch) || !graphType || !idList || !idList.length) {
		return [];
	}
	const graphTypeName = schemaType === SchemaTypes.Filter ? introspectionApi.getFetchTypeName(graphType) : graphType;
	const graphTypemaxVersion = await introspectionApi.getMaxVersionNameForBaseName(api, graphTypeName, schemaType);
	if (!graphTypemaxVersion) { return []; }

	// remove F_ for schema type of Filter and camelize
	const queryName = schemaType === SchemaTypes.Filter ? camelCase(graphTypemaxVersion.substring(2)) : camelCase(graphTypemaxVersion);
	let query = cloneDeep(queryByIds(filter));
	if (attributeNames) {
		const attribs = Array.isArray(attributeNames) ? attributeNames : [attributeNames];
		attribs.forEach(att => {
			// Attributes may be of type string of object
			// For string, just add it as a property to edges with a true value
			if (typeof att === 'string') {
				query.XX_QUERY_NAME_XX.edges.node[att] = true;
			}
			// For object, append the object to the edges
			else if (typeof att === 'object') {
				query.XX_QUERY_NAME_XX.edges.node = {
					...query.XX_QUERY_NAME_XX.edges.node,
					...att,
				}
			}
		});
	}

	query = jsonToGraphQLQuery({ query });
	query = query.replace('XX_QUERY_NAME_XX', queryName);

	const variables = { idList };
	try {
		const response = await graphql.query({
			query,
			api,
			variables,
		});
		const { data, errors } = queryHelper.response(response);
		if (errors) { throw errors; }

		const results = queryHelper.relayToListResponse(data[queryName]);
		return buildResults(results, attributeNames);

	} catch (err) {
		throw err;
	}
};

const buildResults = (results, attributeNames) => {
	const values = results.list.map((value) => {
		const result = { id: value.id };
		const attribs = Array.isArray(attributeNames) ? attributeNames : [attributeNames];
		attribs.forEach((att) => {
			const attributeNamePaths = getTopLevelPathFromAttributeName(att)
			attributeNamePaths.forEach(path => result[path] = getAttribute(value, path));
		});
		return result;
	});
	results.list = values
	return results || [];
}

/**
 * get the top level path name so we can match it with the results coming from DA
 * ex. attributeName = 'names {firstName, fullName}' will return 'names'
 */
const getTopLevelPathFromAttributeName = (attributeName) => {
	if (!attributeName) {
		return [];
	}
	if (typeof attributeName === 'string') {
		return [attributeName.replace(/{.*/gi, '').trim()];
	}
	else if (typeof attributeName === 'object') {
		return Object.keys(attributeName);
	}
}

module.exports = {
	buildResults,
	fetchEedmLookupValues,
	fetchEedmValuesByAttribute,
	fetchEedmValuesByIds,
	getDefaultFilter,
	getTopLevelPathFromAttributeName
}